import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    Font,
    pdf
} from '@react-pdf/renderer';
import { SharedProposalData } from '../types/proposal';
import { formatCurrency } from '../utils/format';
import { formatDateBR } from '../utils/date';

// Registrar fontes
Font.register({
    family: 'Roboto',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

Font.register({
    family: 'Roboto-Bold',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
});

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#fff',
        padding: 40,
        fontFamily: 'Roboto',
    },
    header: {
        marginBottom: 30,
    },
    logo: {
        width: 120,
        marginBottom: 20,
    },
    title: {
        fontSize: 24,
        fontFamily: 'Roboto-Bold',
        marginBottom: 10,
        color: '#1976d2',
    },
    subtitle: {
        fontSize: 14,
        marginBottom: 20,
        color: '#666',
    },
    section: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 18,
        fontFamily: 'Roboto-Bold',
        marginBottom: 10,
        color: '#1976d2',
        borderBottom: '1 solid #eee',
        paddingBottom: 5,
    },
    text: {
        fontSize: 12,
        lineHeight: 1.5,
        marginBottom: 10,
        color: '#333',
    },
    timeline: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 10,
    },
    timelineItem: {
        width: '50%',
        padding: 10,
        marginBottom: 10,
    },
    timelineMonth: {
        fontSize: 14,
        fontFamily: 'Roboto-Bold',
        marginBottom: 5,
        color: '#1976d2',
    },
    financialCard: {
        padding: 15,
        marginBottom: 10,
        backgroundColor: '#f5f5f5',
        borderRadius: 5,
    },
    financialValue: {
        fontSize: 20,
        fontFamily: 'Roboto-Bold',
        color: '#1976d2',
        marginBottom: 5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        textAlign: 'center',
        color: '#666',
        fontSize: 10,
        borderTop: '1 solid #eee',
        paddingTop: 10,
    },
});

interface ProposalPDFProps {
    data: SharedProposalData;
}

const ProposalPDF: React.FC<ProposalPDFProps> = ({ data }) => {
    const formatDate = (dateString: string) => {
        return formatDateBR(dateString);
    };

    const renderDeliverables = (monthDeliverables: string | string[]) => {
        if (Array.isArray(monthDeliverables)) {
            return monthDeliverables.map((deliverable: string, dIndex: number) => (
                <Text key={dIndex} style={styles.text}>
                    • {deliverable}
                </Text>
            ));
        }
        return (
            <Text style={styles.text}>
                • {monthDeliverables}
            </Text>
        );
    };

    return (
        <Document>
            {/* Capa */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Image src="/logo.svg" style={styles.logo} />
                    <Text style={styles.title}>Proposta Comercial</Text>
                    <Text style={styles.subtitle}>{data.project.name}</Text>
                    <Text style={styles.text}>
                        Preparado para: {data.project.client?.name}
                    </Text>
                    <Text style={styles.text}>
                        Data: {formatDate(data.created_at)}
                    </Text>
                </View>
            </Page>

            {/* Conteúdo */}
            <Page size="A4" style={styles.page}>
                {/* Escopo */}
                {data.sections.scope && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Escopo</Text>
                        <Text style={styles.text}>{data.content.scope?.description}</Text>
                        {data.content.scope?.items?.map((item: string, index: number) => (
                            <Text key={index} style={styles.text}>
                                • {item}
                            </Text>
                        ))}
                    </View>
                )}
            </Page>

            {/* Timeline */}
            <Page size="A4" style={styles.page}>
                <Text style={styles.sectionTitle}>Cronograma</Text>
                <View style={styles.timeline}>
                    {data.content.timeline?.deliverables?.map((monthDeliverables, index) => {
                        const monthDate = new Date(data.project.simulation?.start_date || '');
                        monthDate.setMonth(monthDate.getMonth() + index);
                        const monthName = monthDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

                        return (
                            <View key={index} style={styles.timelineItem}>
                                <Text style={styles.timelineMonth}>{monthName}</Text>
                                {renderDeliverables(monthDeliverables)}
                            </View>
                        );
                    })}
                </View>
            </Page>

            {/* Financeiro */}
            <Page size="A4" style={styles.page}>
                <Text style={styles.sectionTitle}>Investimento</Text>
                <View style={styles.section}>
                    <View style={styles.financialCard}>
                        <Text style={styles.text}>Investimento Mensal</Text>
                        <Text style={styles.financialValue}>
                            {formatCurrency((data.project.simulation?.total_revenue || 0) / (data.project.simulation?.duration_months || 1))}
                        </Text>
                        <Text style={styles.text}>por mês</Text>
                    </View>

                    <View style={styles.financialCard}>
                        <Text style={styles.text}>Investimento Total</Text>
                        <Text style={styles.financialValue}>
                            {formatCurrency(data.project.simulation?.total_revenue || 0)}
                        </Text>
                        <Text style={styles.text}>valor total</Text>
                    </View>

                    <View style={styles.financialCard}>
                        <Text style={styles.text}>Duração do Projeto</Text>
                        <Text style={styles.financialValue}>
                            {data.project.simulation?.duration_months || 0}
                        </Text>
                        <Text style={styles.text}>meses</Text>
                    </View>
                </View>

                <View style={styles.footer}>
                    <Text>© {new Date().getFullYear()} Qaracter. Todos os direitos reservados.</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ProposalPDF; 